import { log } from "../../util/log";
import { getSafe } from "../../util/storeHelper";
import { activeGameId, lastActiveProfileForGame, } from "../profile_management/selectors";
import { currentLoadOrderForProfile } from "./selectors";
import { toExtendedLoadOrderEntry } from "./util";
export default class UpdateSet {
    mApi;
    mModEntries = {};
    mExternalEntries = {};
    mInitialized = false;
    mShouldRestore = false;
    mIsFBLO;
    constructor(api, isFBLO) {
        this.mApi = api;
        this.mIsFBLO = isFBLO;
    }
    get shouldRestore() {
        return this.mInitialized && this.mShouldRestore;
    }
    set shouldRestore(value) {
        this.mShouldRestore = value;
    }
    get shouldReset() {
        return this.mInitialized && !this.mShouldRestore;
    }
    forceReset = () => {
        this.reset();
    };
    addEntry = (lo) => {
        const state = this.mApi.getState();
        const gameMode = activeGameId(state);
        const modId = lo.modId ?? lo.id;
        const mod = getSafe(state, ["persistent", "mods", gameMode, modId], undefined);
        if (modId === undefined)
            return;
        let key;
        const numericId = getSafe(mod, ["attributes", "modId"], -1);
        key = numericId !== -1 ? numericId : lo.id;
        const target = lo.modId ? this.mModEntries : this.mExternalEntries;
        if (!target[key]) {
            target[key] = [lo];
        }
        else if (!target[key].some((entry) => entry.id === lo.id)) {
            target[key].push(lo);
        }
    };
    init = (gameId, modEntries) => {
        if (!this.mIsFBLO(gameId) || this.mShouldRestore) {
            return;
        }
        if (this.shouldReset) {
            this.reset();
        }
        this.mInitialized = true;
        modEntries =
            !!modEntries && Array.isArray(modEntries)
                ? modEntries
                : this.genExtendedItemsFromState();
        modEntries.forEach((iter) => this.addEntry(iter));
    };
    genExtendedItemsFromState = () => {
        const state = this.mApi.getState();
        const gameMode = activeGameId(state);
        if (!gameMode) {
            return [];
        }
        const profileId = lastActiveProfileForGame(state, gameMode);
        if (!profileId) {
            return [];
        }
        const loadOrder = currentLoadOrderForProfile(state, profileId);
        if (!loadOrder || !Array.isArray(loadOrder)) {
            return [];
        }
        const extended = loadOrder.map(toExtendedLoadOrderEntry(this.mApi));
        return extended;
    };
    reset = () => {
        this.mModEntries = {};
        this.mExternalEntries = {};
        this.mInitialized = false;
        this.mShouldRestore = false;
    };
    has = (value) => {
        return typeof value === "string"
            ? this.mExternalEntries[value] !== undefined
            : this.mModEntries[value] !== undefined;
    };
    hasEntry = (entry) => {
        return entry.modId
            ? Object.values(this.mModEntries).some((l) => l.some((m) => m.modId === entry.modId || m.id === entry.id))
            : Object.values(this.mExternalEntries).some((l) => l.some((m) => m.id === entry.id));
    };
    restore = (loadOrder) => {
        if (Object.keys(this.mModEntries).length === 0 &&
            Object.keys(this.mExternalEntries).length === 0) {
            // Nothing to restore
            return loadOrder;
        }
        const restoredLO = [...loadOrder];
        const getEntryExt = (entry) => {
            const stored = this.findEntry(entry);
            if (!stored) {
                // This is probably an entry for a manually added mod/native game entry
                //  use the existing index.
                return {
                    ...entry,
                    index: loadOrder.findIndex((l) => l.name === entry.name),
                };
            }
            return stored.entries.find((l) => l.name === entry.name) || null;
        };
        restoredLO.sort((lhs, rhs) => {
            const lhsEntry = getEntryExt(lhs);
            const rhsEntry = getEntryExt(rhs);
            if (!lhsEntry || !rhsEntry) {
                return 0;
            }
            return lhsEntry.index - rhsEntry.index;
        });
        const state = this.mApi.getState();
        const gameMode = activeGameId(state);
        const remapped = restoredLO.map(toExtendedLoadOrderEntry(this.mApi));
        this.init(gameMode, remapped);
        this.mShouldRestore = false;
        return remapped;
    };
    // The modId of the mod we are looking for as stored in Vortex's state.
    findEntry = (lookUpEntry) => {
        if (!this.hasEntry(lookUpEntry)) {
            return null;
        }
        if (lookUpEntry.modId === undefined) {
            // This is an external entry, we need to find it in the external entries.
            const extEntry = Object.entries(this.mExternalEntries).find((entry) => {
                const [eId, loEntries] = entry;
                return Array.isArray(loEntries) && loEntries.some((l) => l.id === lookUpEntry.id);
            });
            if (extEntry !== undefined) {
                return { entries: this.mExternalEntries[extEntry[0]] };
            }
        }
        else {
            const numericId = Object.entries(this.mModEntries).find((entry) => {
                const [nId, loEntries] = entry;
                return Array.isArray(loEntries) && loEntries.some((l) => l.modId === lookUpEntry.modId || l.id === lookUpEntry.id);
            })?.[0];
            if (numericId === undefined) {
                return null;
            }
            return { entries: this.mModEntries[numericId] };
        }
    };
    get = (modId) => {
        if (typeof modId === "string") {
            return this.mExternalEntries[modId] || [];
        }
        else {
            return this.mModEntries[modId] || [];
        }
    };
    add = (x) => {
        log("warn", "Use addEntry", x);
        return;
    };
    isInitialized = () => {
        return this.mInitialized;
    };
}
